import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserFormDialog } from "@/components/UserFormDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, KeyRound, Users as UsersIcon, FolderCog, Loader2, ShieldAlert, Mail, MailPlus } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";

export default function Users() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);

  const [userDialog, setUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetRequireChange, setResetRequireChange] = useState(true);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [resendLink, setResendLink] = useState(null); // null = closed; string = open with link

  const [groupDialog, setGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [groupDesc, setGroupDesc] = useState("");
  const [groupCanDelete, setGroupCanDelete] = useState(false);
  const [groupCanUpload, setGroupCanUpload] = useState(true);
  const [groupCanView, setGroupCanView] = useState(true);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState(null);

  const loadUsers = useCallback(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  const loadGroups = useCallback(() => { api.get("/groups").then((r) => setGroups(r.data)); }, []);
  useEffect(() => { loadUsers(); loadGroups(); }, [loadUsers, loadGroups]);

  const groupName_ = (id) => groups.find((g) => g.id === id)?.name || "—";

  const submitReset = async () => {
    if (resetPwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPwd, require_change: resetRequireChange });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null); setResetPwd("");
      loadUsers();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleForceReset = async (u) => {
    const next = !u.must_change_password;
    try {
      await api.post(`/users/${u.id}/force-password-reset`, { value: next });
      toast.success(next ? `${u.name} must change password at next login` : `Cleared forced reset for ${u.name}`);
      loadUsers();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const confirmDeleteUser = async () => {
    try { await api.delete(`/users/${deleteUserTarget.id}`); toast.success("User deleted"); loadUsers(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteUserTarget(null); }
  };

  const resendInvite = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/resend-invite`);
      if (data.invite?.sent) {
        toast.success(`Invite re-sent to ${u.email}`);
      } else {
        setResendLink(data.invite?.link || "");
        toast.message("Email not delivered — share the invite link below.");
      }
      loadUsers();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const grantLogin = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/grant-login`);
      if (data.invite?.sent) toast.success(`Login access granted — invite sent to ${u.email}`);
      else { setResendLink(data.invite?.link || ""); toast.message("Login granted. Share the invite link below."); }
      loadUsers();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openNewGroup = () => { setEditingGroup(null); setGroupName(""); setGroupDesc(""); setGroupCanDelete(false); setGroupCanUpload(true); setGroupCanView(true); setGroupDialog(true); };
  const openEditGroup = (g) => { setEditingGroup(g); setGroupName(g.name); setGroupDesc(g.description || ""); setGroupCanDelete(Boolean(g.can_delete_files)); setGroupCanUpload(g.can_upload_files !== false); setGroupCanView(g.can_view_files !== false); setGroupDialog(true); };

  const submitGroup = async (e) => {
    e.preventDefault();
    const payload = { name: groupName, description: groupDesc, can_delete_files: groupCanDelete, can_upload_files: groupCanUpload, can_view_files: groupCanView };
    try {
      if (editingGroup) { await api.put(`/groups/${editingGroup.id}`, payload); toast.success("Group updated"); }
      else { await api.post("/groups", payload); toast.success("Group created"); }
      setGroupDialog(false); loadGroups();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const confirmDeleteGroup = async () => {
    try { await api.delete(`/groups/${deleteGroupTarget.id}`); toast.success("Group deleted"); loadGroups(); loadUsers(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteGroupTarget(null); }
  };

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTab(id)} data-testid={`tab-${id}`}
      className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${
        tab === id ? "bg-[#002FA7] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="p-6 lg:p-8" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-gray-900">Employees</h1>
          <p className="text-gray-500 text-sm mt-1">Manage employees and teams (client-access scope).</p>
        </div>
        {tab === "users" ? (
          <button onClick={() => { setEditingUser(null); setUserDialog(true); }} data-testid="add-user-button"
            className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
            <Plus className="w-4 h-4" /> New employee
          </button>
        ) : (
          <button onClick={openNewGroup} data-testid="add-group-button"
            className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
            <Plus className="w-4 h-4" /> New team
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-5">
        <TabBtn id="users" icon={UsersIcon} label={`Employees (${users.length})`} />
        <TabBtn id="groups" icon={FolderCog} label={`Teams (${groups.length})`} />
      </div>

      {tab === "users" && (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden shadow-sm">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {["User", "Role", "Groups", "Sign-in", ""].map((h) => (
                  <th key={h || "a"} className="text-xs font-bold uppercase tracking-wider text-gray-500 py-3 px-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="users-table-body">
              {users.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center font-bold text-xs">{u.name?.[0]?.toUpperCase()}</div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{u.name} {u.id === me?.id && <span className="text-xs text-gray-400">(you)</span>}</p>
                        <p className="text-xs text-gray-500">{u.title ? `${u.title} · ` : ""}{u.email}</p>
                        {u.login_enabled === false && (
                          <span data-testid={`no-login-badge-${u.id}`} className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold bg-gray-100 text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded-sm">
                            Directory only · no login
                          </span>
                        )}
                        {u.must_change_password && (
                          <span data-testid={`must-change-badge-${u.id}`} className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                            <ShieldAlert className="w-3 h-3" /> Must reset at next login
                          </span>
                        )}
                        {u.invite_pending && !u.is_sso && (
                          <span data-testid={`invite-pending-badge-${u.id}`} className="inline-flex items-center gap-1 mt-1 ml-1 text-[11px] font-semibold bg-blue-50 text-[#002FA7] border border-blue-200 px-1.5 py-0.5 rounded-sm">
                            <Mail className="w-3 h-3" /> Invite pending
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm border capitalize ${u.role === "admin" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-[#002FA7] border-blue-200"}`}>{u.role}</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">
                    {(u.group_ids || []).length === 0 ? <span className="text-gray-400">All / none</span> :
                      <div className="flex flex-wrap gap-1">{u.group_ids.map((gid) => <span key={gid} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm">{groupName_(gid)}</span>)}</div>}
                  </td>
                  <td className="py-3 px-4" data-testid={`signin-method-${u.id}`}>
                    {u.login_enabled === false ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400">No login</span>
                    ) : u.is_sso ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                        <svg width="14" height="14" viewBox="0 0 23 23" aria-hidden="true"><path fill="#f25022" d="M1 1h10v10H1z"/><path fill="#7fba00" d="M12 1h10v10H12z"/><path fill="#00a4ef" d="M1 12h10v10H1z"/><path fill="#ffb900" d="M12 12h10v10H12z"/></svg>
                        Microsoft
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                        <KeyRound className="w-3.5 h-3.5" /> Password
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button onClick={() => { setEditingUser(u); setUserDialog(true); }} title="Edit" data-testid={`edit-user-${u.id}`} className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
                    {u.login_enabled === false && (
                      <button onClick={() => grantLogin(u)} title="Grant login access" data-testid={`grant-login-${u.id}`} className="p-1.5 rounded-sm text-gray-400 hover:text-emerald-600 hover:bg-emerald-50"><MailPlus className="w-4 h-4" /></button>
                    )}
                    {!u.is_sso && u.login_enabled !== false && (
                      <>
                        {u.invite_pending && (
                          <button onClick={() => resendInvite(u)} title="Resend invite" data-testid={`resend-invite-${u.id}`} className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50"><MailPlus className="w-4 h-4" /></button>
                        )}
                        <button onClick={() => { setResetTarget(u); setResetPwd(""); setResetRequireChange(true); }} title="Reset password" data-testid={`reset-user-${u.id}`} className="p-1.5 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50"><KeyRound className="w-4 h-4" /></button>
                        <button onClick={() => toggleForceReset(u)} title={u.must_change_password ? "Clear forced reset" : "Force reset at next login"} data-testid={`force-reset-user-${u.id}`} className={`p-1.5 rounded-sm hover:bg-amber-50 ${u.must_change_password ? "text-amber-600" : "text-gray-400 hover:text-amber-600"}`}><ShieldAlert className="w-4 h-4" /></button>
                      </>
                    )}
                    <button onClick={() => setDeleteUserTarget(u)} title="Delete" data-testid={`delete-user-${u.id}`} className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "groups" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="groups-list">
          {groups.length === 0 && <p className="text-sm text-gray-400">No groups yet. Create one to scope client access.</p>}
          {groups.map((g) => (
            <div key={g.id} className="bg-white border border-gray-200 rounded-sm shadow-sm p-5" data-testid={`group-card-${g.id}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-heading font-bold text-gray-900">{g.name}</h3>
                <div className="flex gap-1">
                  <button onClick={() => openEditGroup(g)} data-testid={`edit-group-${g.id}`} className="p-1 rounded-sm text-gray-400 hover:text-[#002FA7] hover:bg-blue-50"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => setDeleteGroupTarget(g)} data-testid={`delete-group-${g.id}`} className="p-1 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">{g.description || "No description"}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {g.can_delete_files && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-sm" data-testid={`group-candelete-badge-${g.id}`}>
                    <Trash2 className="w-3 h-3" /> Can delete
                  </span>
                )}
                {g.can_upload_files === false && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-sm" data-testid={`group-noupload-badge-${g.id}`}>
                    No upload
                  </span>
                )}
                {g.can_view_files === false && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-sm" data-testid={`group-noview-badge-${g.id}`}>
                    No download
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <UserFormDialog open={userDialog} onOpenChange={setUserDialog} userRecord={editingUser} groups={groups} onSaved={loadUsers} />

      {/* Resend invite link fallback (when email delivery is unavailable) */}
      <Dialog open={resendLink !== null} onOpenChange={(o) => !o && setResendLink(null)}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="resend-invite-dialog">
          <DialogHeader><DialogTitle className="font-heading">Invite link</DialogTitle>
            <DialogDescription className="sr-only">Copy and share this invite link.</DialogDescription></DialogHeader>
          <p className="text-sm text-gray-500">Email delivery is unavailable. Share this secure “set your password” link with the user:</p>
          <div className="flex gap-2">
            <input readOnly value={resendLink || ""} data-testid="resend-invite-link-input"
              className="w-full border border-gray-300 rounded-sm px-3 py-2 text-xs focus:outline-none" />
            <button type="button" onClick={() => { navigator.clipboard?.writeText(resendLink || ""); toast.success("Copied"); }}
              data-testid="resend-invite-copy" className="bg-[#002FA7] text-white px-3 rounded-sm text-xs font-semibold shrink-0">Copy</button>
          </div>
          <DialogFooter>
            <button onClick={() => setResendLink(null)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={Boolean(resetTarget)} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="sm:max-w-sm rounded-sm" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reset password</DialogTitle>
            <DialogDescription className="sr-only">Set a new password for this user.</DialogDescription></DialogHeader>
          <p className="text-sm text-gray-500">Set a new password for <strong>{resetTarget?.name}</strong> and share it with them.</p>
          <input data-testid="reset-password-input" type="text" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="New password (min 6 chars)" className={inputCls} />
          <label className="flex items-start gap-2.5 mt-1 cursor-pointer" data-testid="reset-require-change-toggle">
            <input type="checkbox" checked={resetRequireChange} onChange={(e) => setResetRequireChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#002FA7]" data-testid="reset-require-change-checkbox" />
            <span className="text-sm text-gray-700">Require this user to change password at next login</span>
          </label>
          <DialogFooter>
            <button onClick={() => setResetTarget(null)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
            <button onClick={submitReset} data-testid="confirm-reset-button" className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">Reset</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group create/edit dialog */}
      <Dialog open={groupDialog} onOpenChange={setGroupDialog}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="group-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingGroup ? "Edit group" : "New group"}</DialogTitle>
            <DialogDescription className="sr-only">Name and describe the access group.</DialogDescription></DialogHeader>
          <form onSubmit={submitGroup} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
              <input data-testid="group-name-input" required value={groupName} onChange={(e) => setGroupName(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              <input data-testid="group-desc-input" value={groupDesc} onChange={(e) => setGroupDesc(e.target.value)} className={inputCls} />
            </div>
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50" data-testid="group-can-delete-toggle">
              <input type="checkbox" checked={groupCanDelete} onChange={(e) => setGroupCanDelete(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#002FA7]" data-testid="group-can-delete-checkbox" />
              <span className="text-sm">
                <span className="font-semibold text-gray-800">Can delete files</span>
                <span className="block text-xs text-gray-500">Members of this group may remove file attachments. Admins can always delete.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50" data-testid="group-can-upload-toggle">
              <input type="checkbox" checked={groupCanUpload} onChange={(e) => setGroupCanUpload(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#002FA7]" data-testid="group-can-upload-checkbox" />
              <span className="text-sm">
                <span className="font-semibold text-gray-800">Can upload files</span>
                <span className="block text-xs text-gray-500">Members may attach new files to clients. Uncheck to make this group read-only for uploads.</span>
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-sm cursor-pointer hover:bg-gray-50" data-testid="group-can-view-toggle">
              <input type="checkbox" checked={groupCanView} onChange={(e) => setGroupCanView(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#002FA7]" data-testid="group-can-view-checkbox" />
              <span className="text-sm">
                <span className="font-semibold text-gray-800">Can download / view files</span>
                <span className="block text-xs text-gray-500">Members may open and download attachments. Uncheck to hide file contents from this group.</span>
              </span>
            </label>
            <DialogFooter>
              <button type="button" onClick={() => setGroupDialog(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
              <button type="submit" data-testid="group-save-button" className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">{editingGroup ? "Save" : "Create"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteUserTarget)} onOpenChange={(o) => !o && setDeleteUserTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete user?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteUserTarget?.name}</strong> will lose access immediately.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteUser} data-testid="confirm-delete-user-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(deleteGroupTarget)} onOpenChange={(o) => !o && setDeleteGroupTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-group-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete group?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteGroupTarget?.name}" will be removed from all users and clients.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteGroup} data-testid="confirm-delete-group-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
